/**
 * The option shape `Combobox` and `Select` share, and how both read a label and an
 * identity off one — asked and answered in one place so the two cannot drift.
 */

/** The default option shape: `label` is what is shown, `value` identifies. */
export type Option = {
    value: string;
    label: string;
};

/** A labelled section of options; `value` is both its key and its heading. */
export type OptionGroup<Item> = {
    value: string;
    items: readonly Item[];
};

/**
 * The key an option is picked by: its `value`, or the option itself when it is a plain
 * string. A union of string literals survives, so an enum-valued option list hands the
 * enum back rather than a bare `string`.
 */
export type OptionKey<Item> = Item extends string
    ? Item
    : Item extends { value: infer Key extends string }
      ? Key
      : string;

function hasKey<Key extends string>(item: unknown, key: Key): item is Record<Key, unknown> {
    return typeof item === 'object' && item !== null && key in item;
}

/** What an option says: its `label`, or the option itself when it is a plain string. */
export function optionLabel(item: unknown): string {
    if (typeof item === 'string') {
        return item;
    }

    if (hasKey(item, 'label')) {
        return String(item.label);
    }

    return String(item);
}

/** What identifies an option: its `value`, or the option itself when it is a plain string. */
export function optionValue(item: unknown): string {
    if (typeof item === 'string') {
        return item;
    }

    if (hasKey(item, 'value')) {
        return String(item.value);
    }

    return String(item);
}

/** Object options are rebuilt on every render, so identity has to come from `value`. */
export function isSameOption(item: unknown, other: unknown): boolean {
    if (hasKey(item, 'value') && hasKey(other, 'value')) {
        return Object.is(item.value, other.value);
    }

    return Object.is(item, other);
}

/** Whether an `items` list holds sections rather than options. */
export function isGrouped<Item>(
    items: readonly Item[] | readonly OptionGroup<Item>[] | undefined,
): items is readonly OptionGroup<Item>[] {
    return items !== undefined && items.length > 0 && hasKey(items[0], 'items');
}

/** The options an `items` list holds, whether or not they are grouped into sections. */
export function flattenOptions<Item>(
    items: readonly Item[] | readonly OptionGroup<Item>[] | undefined,
): readonly Item[] {
    if (items === undefined) {
        return [];
    }

    return isGrouped(items) ? items.flatMap((group) => group.items) : items;
}
